import { aniNavRegistry, type AniNavTargetSummary } from "@/components/ui/ani-nav";
import { windowRef } from "@/services/agent/aniRef";
import type { OsWindow, OsWorkspace } from "./OsContext";

export interface OsAniContextSnapshot {
  workspaces: Array<{
    id: string;
    name: string;
    isActive: boolean;
    windows: Array<{
      id: string;
      /** Ref for fe_show / fe_close (e.g. portfolio:port-1, app:meetings). */
      ref: string;
      appId: string;
      title: string;
      isFocused: boolean;
      isMinimized: boolean;
      isMaximized: boolean;
      params: Record<string, string>;
    }>;
  }>;
  focusedApp: string;
  focusedWindow: {
    id: string;
    ref: string;
    appId: string;
    title: string;
    params: Record<string, string>;
    /** Tabs / sections / actions in this window, as paths for fe_show. */
    nav_targets: AniNavTargetSummary[];
  } | null;
  /** Per-turn: APP_REGISTRY apps the user can open (dock module rule). */
  available_app_ids?: string[];
}

/** Inputs needed to build a fresh OS context snapshot (e.g. at agent send time). */
export interface OsAniSnapshotSource {
  workspaces: OsWorkspace[];
  activeWorkspaceId: string;
  windowIdsByWorkspace: Map<string, string[]>;
  getWindow: (id: string) => OsWindow | undefined;
  /** Live window list from the OS store (use for FE open verification without waiting for React render). */
  getWindowsSnapshot?: () => readonly OsWindow[];
  getFocusedWindow: () => OsWindow | undefined;
  focusedAppIdInActiveWorkspace: string | null;
}

/** Build OS context for the in-app agent from a point-in-time snapshot (no full windows subscription). */
export function buildOsAniContextSnapshot(
  workspaces: OsWorkspace[],
  activeWorkspaceId: string,
  windowIdsByWorkspace: Map<string, string[]>,
  getWindow: (id: string) => OsWindow | undefined,
  getFocusedWindow: () => OsWindow | undefined,
  focusedAppId: string | null,
): OsAniContextSnapshot {
  const focusedWindow = getFocusedWindow();
  const focusedApp = focusedAppId ?? "home";

  return {
    workspaces: workspaces.map((ws) => {
      const windowIds = windowIdsByWorkspace.get(ws.id) ?? [];
      return {
        id: ws.id,
        name: ws.name,
        isActive: ws.id === activeWorkspaceId,
        windows: windowIds.flatMap((windowId) => {
          const w = getWindow(windowId);
          if (!w) return [];
          return [
            {
              id: w.id,
              ref: windowRef(w),
              appId: w.appId,
              title: w.title,
              isFocused:
                ws.id === activeWorkspaceId && w.id === focusedWindow?.id,
              isMinimized: w.isMinimized,
              isMaximized: w.isMaximized,
              params: w.params,
            },
          ];
        }),
      };
    }),
    focusedApp,
    focusedWindow: focusedWindow
      ? {
          id: focusedWindow.id,
          ref: windowRef(focusedWindow),
          appId: focusedWindow.appId,
          title: focusedWindow.title,
          params: focusedWindow.params,
          nav_targets: aniNavRegistry.list(focusedWindow.id),
        }
      : null,
  };
}
